import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ImagePreviewModalProps {
  isOpen: boolean;
  imageUrl: string | null;
  onClose: () => void;
  title?: string;
  images?: { url: string; title?: string }[];
  currentIndex?: number;
  onImageChange?: (index: number) => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  imageUrl,
  onClose,
  title = 'Preview',
  images = [],
  currentIndex = 0,
  onImageChange,
}) => {
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(
    null
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fromImage, setFromImage] = useState(imageUrl);
  const [toImage, setToImage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSlideDirection(null);
      setIsTransitioning(false);
      setFromImage(imageUrl);
      setToImage(null);
    }
  }, [isOpen, imageUrl]);

  if (!imageUrl) return null;

  const handlePrevious = () => {
    if (images.length > 0 && currentIndex > 0 && !isTransitioning) {
      const prevImage = images[currentIndex - 1].url;
      setToImage(prevImage);
      setSlideDirection('right');
      setIsTransitioning(true);
      setTimeout(() => {
        setFromImage(prevImage);
        setToImage(null);
        setSlideDirection(null);
        setIsTransitioning(false);
        onImageChange?.(currentIndex - 1);
      }, 300);
    }
  };

  const handleNext = () => {
    if (
      images.length > 0 &&
      currentIndex < images.length - 1 &&
      !isTransitioning
    ) {
      const nextImageUrl = images[currentIndex + 1].url;
      setToImage(nextImageUrl);
      setSlideDirection('left');
      setIsTransitioning(true);
      setTimeout(() => {
        setFromImage(nextImageUrl);
        setToImage(null);
        setSlideDirection(null);
        setIsTransitioning(false);
        onImageChange?.(currentIndex + 1);
      }, 300);
    }
  };

  const canNavigate = images.length > 1;

  // Tính toán transform cho container
  let containerStyle: React.CSSProperties = {};
  if (toImage && slideDirection === 'left') {
    containerStyle = {
      transform: 'translateX(-100%)',
      transition: 'transform 300ms ease-in-out',
    };
  } else if (toImage && slideDirection === 'right') {
    containerStyle = {
      transform: 'translateX(100%)',
      transition: 'transform 300ms ease-in-out',
    };
  } else {
    containerStyle = {
      transform: 'translateX(0%)',
      transition: toImage ? 'transform 300ms ease-in-out' : undefined,
    };
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[95vh] overflow-hidden p-0 border-0 bg-black"
        style={{ maxWidth: '90vw', width: '90vw' }}
      >
        <DialogHeader className="absolute top-4 left-4 z-10 bg-black bg-opacity-70 rounded-lg px-4 py-2">
          <DialogTitle className="text-white text-lg font-medium">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-[90vh] flex items-center justify-center bg-black overflow-hidden">
          <div
            className="flex w-full h-full"
            style={{
              width: toImage ? '200%' : '100%',
              ...containerStyle,
            }}
          >
            <div className="w-full h-full flex-shrink-0 flex-grow-0 flex items-center justify-center">
              <img
                src={fromImage || ''}
                alt={title}
                className="w-full h-full object-contain"
              />
            </div>
            {toImage && (
              <div className="w-full h-full flex-shrink-0 flex-grow-0 flex items-center justify-center">
                <img
                  src={toImage}
                  alt={title}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          {canNavigate && (
            <>
              <button
                onClick={handlePrevious}
                disabled={currentIndex === 0 || isTransitioning}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-3 rounded-full transition-all duration-200 cursor-pointer z-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={handleNext}
                disabled={currentIndex === images.length - 1 || isTransitioning}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-3 rounded-full transition-all duration-200 cursor-pointer z-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-3 rounded-full transition-all duration-200 cursor-pointer z-10"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal;
